<template>
  <div class="agenda-view flex flex-col h-full bg-white dark:bg-slate-900">
    <!-- Header -->
    <header class="agenda-view__header flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Agenda
        </h1>
        <span
          v-if="isConnected"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-green-500" />
          {{ googleEmail }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          v-if="isConnected"
          class="agenda-view__new-event-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          @click="openCreateModal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Novo evento
        </button>
        <GoogleCalendarConnect
          v-if="selectedFunnel"
          :funnel-id="selectedFunnel.id"
          :compact="true"
          @connected="onConnected"
          @disconnected="onDisconnected"
        />
      </div>
    </header>

    <!-- Calendar body -->
    <div class="agenda-view__body flex-1 overflow-auto">
      <div v-if="!selectedFunnel" class="flex items-center justify-center h-full">
        <div class="text-center p-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mx-auto mb-4 text-slate-300 dark:text-slate-600"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" /></svg>
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            Selecione um funil na sidebar para visualizar a agenda
          </p>
        </div>
      </div>

      <AgendaTab
        v-else
        :columns="columns"
        :google-events="googleEvents"
        :is-standalone="true"
        @create-event="openCreateModal"
        @edit-event="openEditModal"
        @delete-event="handleDeleteEvent"
      />
    </div>

    <!-- Event Form Modal -->
    <EventFormModal
      v-if="showEventModal"
      :event="editingEvent"
      :funnel-id="selectedFunnel?.id"
      :is-editing="!!editingEvent?.id"
      @save="handleSaveEvent"
      @delete="handleDeleteEvent"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import AgendaTab from './components/AgendaTab.vue';
import GoogleCalendarConnect from './components/GoogleCalendarConnect.vue';
import EventFormModal from './components/EventFormModal.vue';
import googleCalendarAPI from 'dashboard/api/googleCalendar.js';

const store = useStore();
const route = useRoute();

const googleEvents = ref([]);
const isConnected = ref(false);
const googleEmail = ref('');
const showEventModal = ref(false);
const editingEvent = ref(null);
const isLoading = ref(false);

const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

const columns = computed(() => {
  const funnel = selectedFunnel.value;
  if (!funnel) return [];
  return funnel.stages || [];
});

// Check integration status
const checkStatus = async () => {
  if (!selectedFunnel.value) return;
  try {
    const { data } = await googleCalendarAPI.getStatus(selectedFunnel.value.id);
    isConnected.value = data.connected || false;
    googleEmail.value = data.integration?.google_email || '';
    if (isConnected.value) {
      await fetchEvents();
    } else {
      googleEvents.value = [];
    }
  } catch {
    isConnected.value = false;
    googleEvents.value = [];
  }
};

// Fetch events from Google Calendar
const fetchEvents = async () => {
  if (!selectedFunnel.value || !isConnected.value) return;
  isLoading.value = true;
  try {
    const timeMin = new Date();
    timeMin.setDate(timeMin.getDate() - 30);
    const timeMax = new Date();
    timeMax.setDate(timeMax.getDate() + 60);

    const { data } = await googleCalendarAPI.getEvents(
      selectedFunnel.value.id,
      timeMin.toISOString(),
      timeMax.toISOString()
    );
    googleEvents.value = data.events || [];
  } catch (err) {
    console.error('[AgendaView] Failed to fetch events:', err);
    googleEvents.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Modal handlers
const openCreateModal = (dateInfo) => {
  // Guard: dateInfo may be a MouseEvent from @click — only use if it's a string or Date
  const validDate = dateInfo && !(dateInfo instanceof Event) ? dateInfo : null;
  editingEvent.value = validDate ? { start_date: validDate } : null;
  showEventModal.value = true;
};

const openEditModal = (event) => {
  editingEvent.value = event;
  showEventModal.value = true;
};

const closeModal = () => {
  showEventModal.value = false;
  editingEvent.value = null;
};

// CRUD handlers
const handleSaveEvent = async (eventData) => {
  if (!selectedFunnel.value) return;
  try {
    if (editingEvent.value?.id) {
      await googleCalendarAPI.updateEvent(
        selectedFunnel.value.id,
        editingEvent.value.id,
        eventData
      );
    } else {
      await googleCalendarAPI.createEvent(
        selectedFunnel.value.id,
        eventData
      );
    }
    closeModal();
    await fetchEvents();
  } catch (err) {
    console.error('[AgendaView] Failed to save event:', err);
  }
};

const handleDeleteEvent = async (eventId) => {
  if (!selectedFunnel.value || !eventId) return;
  try {
    await googleCalendarAPI.deleteEvent(selectedFunnel.value.id, eventId);
    closeModal();
    await fetchEvents();
  } catch (err) {
    console.error('[AgendaView] Failed to delete event:', err);
  }
};

// Lifecycle
const onConnected = () => {
  isConnected.value = true;
  checkStatus();
};

const onDisconnected = () => {
  isConnected.value = false;
  googleEvents.value = [];
  googleEmail.value = '';
};

onMounted(() => {
  // Set funnel from query param if present
  const funnelIdFromQuery = route.query.funnel;
  if (funnelIdFromQuery) {
    store.dispatch('funnel/setSelectedFunnel', Number(funnelIdFromQuery));
  }
  checkStatus();
});

watch(selectedFunnel, () => {
  checkStatus();
});
</script>

<style scoped>
.agenda-view {
  min-height: 0;
}

.agenda-view__header {
  flex-shrink: 0;
}

.agenda-view__body {
  min-height: 0;
}
</style>
